import { useState, useEffect } from 'react'
import {
  Card,
  Title,
  Text,
  TextInput,
  Button,
  Flex,
  Callout,
  Badge,
} from '@tremor/react'
import {
  XMarkIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateOperator } from '@/api/operators'

interface AudioConfigModalProps {
  isOpen: boolean
  onClose: () => void
  operatorId: number
  operatorName: string
  currentInputDevice: string | null
  currentOutputDevice: string | null
  expectedInputDevice: string | null
  expectedOutputDevice: string | null
  usbAudioDevice: string | null
}

export function AudioConfigModal({
  isOpen,
  onClose,
  operatorId,
  operatorName,
  currentInputDevice,
  currentOutputDevice,
  expectedInputDevice,
  expectedOutputDevice,
  usbAudioDevice,
}: AudioConfigModalProps) {
  const queryClient = useQueryClient()
  const [inputDevice, setInputDevice] = useState(expectedInputDevice || '')
  const [outputDevice, setOutputDevice] = useState(expectedOutputDevice || '')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    setInputDevice(expectedInputDevice || '')
    setOutputDevice(expectedOutputDevice || '')
  }, [expectedInputDevice, expectedOutputDevice])

  const mutation = useMutation({
    mutationFn: () => updateOperator(operatorId, {
      expected_input_device: inputDevice || null,
      expected_output_device: outputDevice || null,
    }),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Configuration audio enregistrée' })
      queryClient.invalidateQueries({ queryKey: ['machines-with-metrics'] })
      queryClient.invalidateQueries({ queryKey: ['operators'] })
      setTimeout(() => {
        setMessage(null)
        onClose()
      }, 1500)
    },
    onError: (error: Error) => {
      setMessage({ type: 'error', text: error.message || 'Erreur lors de la sauvegarde' })
    },
  })

  const handleSave = () => {
    mutation.mutate()
  }

  const handleUseCurrentInput = () => {
    if (currentInputDevice) {
      setInputDevice(currentInputDevice)
    }
  }

  const handleUseCurrentOutput = () => {
    if (currentOutputDevice) {
      setOutputDevice(currentOutputDevice)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
      <Card className="w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
        <Flex justifyContent="between" alignItems="start" className="mb-4">
          <div>
            <Title>Configuration Audio</Title>
            <Text className="text-gray-500">{operatorName}</Text>
          </div>
          <Button
            variant="light"
            icon={XMarkIcon}
            onClick={onClose}
            className="p-1"
          />
        </Flex>

        <Callout
          title="Détection du casque"
          icon={InformationCircleIcon}
          color="blue"
          className="mb-4"
        >
          Configurez le nom du périphérique audio attendu (casque USB).
          Un warning sera affiché si l'opérateur utilise un autre périphérique.
        </Callout>

        {message && (
          <Callout
            title={message.type === 'success' ? 'Succès' : 'Erreur'}
            icon={message.type === 'success' ? CheckCircleIcon : ExclamationTriangleIcon}
            color={message.type === 'success' ? 'green' : 'red'}
            className="mb-4"
          >
            {message.text}
          </Callout>
        )}

        <div className="space-y-6">
          {/* Input Device (Microphone) */}
          <div>
            <Flex justifyContent="between" className="mb-2">
              <Flex justifyContent="start" className="gap-2">
                <MicrophoneIcon className="w-5 h-5 text-gray-500" />
                <Text className="font-medium">Microphone attendu</Text>
              </Flex>
              {currentInputDevice && (
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={handleUseCurrentInput}
                >
                  Utiliser actuel
                </Button>
              )}
            </Flex>
            <TextInput
              placeholder="Ex: USB Audio, Jabra, Plantronics..."
              value={inputDevice}
              onChange={(e) => setInputDevice(e.target.value)}
            />
            {currentInputDevice && (
              <Flex justifyContent="start" className="gap-2 mt-2">
                <Text className="text-xs text-gray-500">Actuel:</Text>
                <Badge color={
                  !inputDevice ? 'gray' :
                  inputDevice.toLowerCase() && currentInputDevice.toLowerCase().includes(inputDevice.toLowerCase()) ? 'green' : 'red'
                }>
                  {currentInputDevice}
                </Badge>
              </Flex>
            )}
            {usbAudioDevice && (
              <Flex justifyContent="start" className="gap-2 mt-2">
                <Text className="text-xs text-gray-500">USB:</Text>
                <Badge color="blue">{usbAudioDevice}</Badge>
                <Button
                  size="xs"
                  variant="light"
                  color="blue"
                  onClick={() => setInputDevice(usbAudioDevice)}
                >
                  Utiliser USB
                </Button>
              </Flex>
            )}
          </div>

          {/* Output Device (Speaker) */}
          <div>
            <Flex justifyContent="between" className="mb-2">
              <Flex justifyContent="start" className="gap-2">
                <SpeakerWaveIcon className="w-5 h-5 text-gray-500" />
                <Text className="font-medium">Sortie audio attendue</Text>
              </Flex>
              {currentOutputDevice && (
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={handleUseCurrentOutput}
                >
                  Utiliser actuel
                </Button>
              )}
            </Flex>
            <TextInput
              placeholder="Ex: USB Audio, Jabra, Plantronics..."
              value={outputDevice}
              onChange={(e) => setOutputDevice(e.target.value)}
            />
            {currentOutputDevice && (
              <Flex justifyContent="start" className="gap-2 mt-2">
                <Text className="text-xs text-gray-500">Actuel:</Text>
                <Badge color={
                  !outputDevice ? 'gray' :
                  outputDevice.toLowerCase() && currentOutputDevice.toLowerCase().includes(outputDevice.toLowerCase()) ? 'green' : 'red'
                }>
                  {currentOutputDevice}
                </Badge>
              </Flex>
            )}
            {usbAudioDevice && (
              <Flex justifyContent="start" className="gap-2 mt-2">
                <Text className="text-xs text-gray-500">USB:</Text>
                <Badge color="blue">{usbAudioDevice}</Badge>
                <Button
                  size="xs"
                  variant="light"
                  color="blue"
                  onClick={() => setOutputDevice(usbAudioDevice)}
                >
                  Utiliser USB
                </Button>
              </Flex>
            )}
          </div>
        </div>

        <Flex justifyContent="end" className="gap-2 mt-6 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            loading={mutation.isPending}
          >
            Enregistrer
          </Button>
        </Flex>
      </Card>
    </div>
  )
}
